import React from 'react';
import { Container } from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ProductCard } from '@/components/product/card';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';

const Favorites = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Fetch user's favorite listings
  const { data: favorites = [], isLoading, error } = useQuery({
    queryKey: ['userFavorites', user?.id],
    queryFn: async () => {
      try {
        if (!user) return [];
        
        // Get favorite listing IDs
        const { data: favoriteData, error: favoriteError } = await supabase
          .from('favorites')
          .select('listing_id')
          .eq('user_id', user.id);
        
        if (favoriteError) {
          throw favoriteError;
        }
        
        if (!favoriteData || favoriteData.length === 0) {
          return [];
        }
        
        // Get the actual listings using the favorite IDs
        const listingIds = favoriteData.map(fav => fav.listing_id);
        
        const { data: listings, error: listingsError } = await supabase
          .from('listings')
          .select('*, category:categories(id, name)')
          .in('id', listingIds);
        
        if (listingsError) {
          throw listingsError;
        }
        
        // Get the usernames for all listings
        const userIds = listings.map(listing => listing.user_id).filter(Boolean);
        
        let usernamesMap = {};
        if (userIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username')
            .in('id', userIds);
            
          if (!profilesError && profiles) {
            usernamesMap = profiles.reduce((acc, profile) => {
              acc[profile.id] = profile.username;
              return acc;
            }, {});
          }
        }
        
        return listings.map(listing => ({
          id: listing.id.toString(),
          title: listing.title,
          price: listing.price,
          location: listing.location,
          imageUrl: listing.images && listing.images.length > 0 ? listing.images[0] : '',
          category: listing.category?.name || 'Pa kategori',
          createdAt: listing.created_at,
          isPremium: listing.is_premium,
          username: usernamesMap[listing.user_id] || 'Përdorues'
        }));
      } catch (error) {
        return [];
      }
    },
    enabled: !!user && isAuthenticated,
  });
  
  // Handle go back
  const handleGoBack = () => {
    navigate(-1);
  };
  
  // Check if we have any favorites
  const hasFavorites = Array.isArray(favorites) && favorites.length > 0;
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow">
        <Container>
          <div className="py-10">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="outline" size="icon" onClick={handleGoBack} className="rounded-full">
                <ArrowLeft size={16} />
              </Button>
              <h1 className="text-3xl font-bold">Të Preferuarat Tuaja</h1>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-pulse flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-6 py-1">
                    <div className="h-2 bg-gray-200 rounded"></div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="h-2 bg-gray-200 rounded col-span-2"></div>
                        <div className="h-2 bg-gray-200 rounded col-span-1"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <Card className="border border-red-200 bg-red-50">
                <CardContent className="text-center py-8">
                  <h3 className="text-xl font-medium text-red-700 mb-2">Gabim në ngarkim</h3>
                  <p className="text-red-600 mb-6">Ndodhi një gabim gjatë ngarkimit të artikujve të preferuar.</p>
                  <Button onClick={() => navigate('/')} className="bg-brand-500 hover:bg-brand-600">
                    Kthehu te Kryefaqja
                  </Button>
                </CardContent>
              </Card>
            ) : hasFavorites ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {favorites.map((listing) => (
                  <ProductCard
                    key={listing.id}
                    id={listing.id}
                    title={listing.title}
                    price={listing.price}
                    location={listing.location}
                    imageUrl={listing.imageUrl}
                    category={listing.category}
                    createdAt={listing.createdAt}
                    isPremium={listing.isPremium}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-gray-200 rounded-lg bg-white shadow-sm">
                <h3 className="text-xl font-medium text-gray-700 mb-2">Nuk keni shtuar asnjë artikull te të preferuarat</h3>
                <p className="text-gray-500 mb-6">Shfletoni shpalljet dhe klikoni ikonën e zemrës për t'i shtuar ato te të preferuarat</p>
                <Button onClick={() => navigate('/')} className="bg-brand-500 hover:bg-brand-600">
                  Shfletoni Shpalljet
                </Button>
              </div>
            )}
          </div>
        </Container>
      </main>
      
      <Footer />
    </div>
  );
};

export default Favorites;